import { SlideshowSelectEvent, ThemeEvents } from '@theme/events';

/** @type {number} */
let variantFxToken = 0;

/** @type {boolean} */
let suppressSlideBurst = false;

const DEFAULT_FX_RGB = '120, 158, 216';
const WHITE_FX_RGB = '168, 168, 172';

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {boolean}
 */
function isNearWhite(r, g, b) {
  const min = Math.min(r, g, b);
  const max = Math.max(r, g, b);
  return min >= 230 && max >= 245;
}

/**
 * @param {string} color
 * @returns {[number, number, number] | null}
 */
function parseColorToRgb(color) {
  if (!color || color.startsWith('url(')) return null;

  const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)/i);
  if (rgbMatch) {
    return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
  }

  if (typeof document === 'undefined') return null;

  const probe = document.createElement('span');
  probe.style.color = color;
  document.body.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  probe.remove();

  const computedMatch = computed.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!computedMatch) return null;

  return [Number(computedMatch[1]), Number(computedMatch[2]), Number(computedMatch[3])];
}

/**
 * @param {Element} swatch
 * @returns {[number, number, number] | null}
 */
function getSwatchRgb(swatch) {
  if (!(swatch instanceof HTMLElement)) return null;

  const inlineBackground = swatch.style.getPropertyValue('--swatch-background').trim();
  const computedBackground = getComputedStyle(swatch).getPropertyValue('--swatch-background').trim();
  return parseColorToRgb(inlineBackground || computedBackground);
}

/**
 * @param {ParentNode} scope
 * @returns {Element | null}
 */
function getColorSwatchesFieldset(scope) {
  const pickers = scope.querySelectorAll('variant-picker[data-template-product-match="true"], variant-picker');

  for (const picker of pickers) {
    const fieldsets = picker.querySelectorAll('fieldset.variant-option--swatches');

    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase();
      if (
        legend === 'color' ||
        legend === 'colour' ||
        legend === 'couleur' ||
        legend === 'farbe' ||
        legend === 'colore'
      ) {
        return fieldset;
      }
    }

    if (fieldsets.length > 0) {
      return fieldsets[0];
    }
  }

  const stickyFieldset = scope.querySelector('[data-sticky-variant-picker] fieldset.variant-option--swatches');
  return stickyFieldset ?? null;
}

/**
 * @param {ParentNode} scope
 * @returns {string}
 */
function getSelectedVariantFxRgb(scope) {
  const fieldset = getColorSwatchesFieldset(scope);
  const checkedInput = fieldset?.querySelector('input:checked');
  const swatch = checkedInput?.closest('label')?.querySelector('.swatch');
  const rgb = swatch ? getSwatchRgb(swatch) : null;

  if (!rgb) return DEFAULT_FX_RGB;

  const [r, g, b] = rgb;
  if (isNearWhite(r, g, b)) return WHITE_FX_RGB;

  return `${r}, ${g}, ${b}`;
}

/**
 * @param {Element} scope
 */
function updateFxColor(scope) {
  if (!(scope instanceof HTMLElement)) return;

  const rgb = getSelectedVariantFxRgb(scope);
  scope.style.setProperty('--drywell-blue', rgb);

  const [r, g, b] = rgb.split(',').map((value) => Number(value.trim()));
  scope.style.setProperty('--drywell-blue-solid', `rgb(${r}, ${g}, ${b})`);
}

/**
 * @param {Element} slot
 * @returns {Element[]}
 */
function getSlides(slot) {
  return Array.from(slot.querySelectorAll('slideshow-slide'));
}

/**
 * @param {Element} slot
 * @returns {import('./slideshow').Slideshow | null}
 */
function getSlideshow(slot) {
  const el = slot.querySelector('slideshow-component');
  return el ?? null;
}

/**
 * Resolve the active slide index.
 * Prefer slideshow.current / forced index — during transitions multiple slides can have
 * aria-hidden="false", so "first visible" wrongly keeps the trust banner on later slides.
 *
 * @param {Element} slot
 * @param {number} [forcedIndex]
 * @returns {number}
 */
function getActiveSlideIndex(slot, forcedIndex) {
  if (typeof forcedIndex === 'number' && Number.isFinite(forcedIndex)) {
    return forcedIndex;
  }

  const slideshow = getSlideshow(slot);
  if (slideshow && typeof slideshow.current === 'number') {
    return slideshow.current;
  }

  const slides = getSlides(slot);
  if (slides.length === 0) return 0;

  const selectedThumb = slot.querySelector(
    'slideshow-controls[thumbnails] .slideshow-controls__thumbnail[aria-selected="true"], slideshow-controls[thumbnails] button[aria-current="true"]'
  );
  if (selectedThumb) {
    const thumbIndex = Array.from(
      slot.querySelectorAll(
        'slideshow-controls[thumbnails] .slideshow-controls__thumbnail, slideshow-controls[thumbnails] button'
      )
    ).indexOf(selectedThumb);
    if (thumbIndex >= 0) return thumbIndex;
  }

  const visibleIndex = slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
  return visibleIndex >= 0 ? visibleIndex : 0;
}

/**
 * @param {Element} slot
 * @returns {Element | null}
 */
function getActiveSlide(slot) {
  const slides = getSlides(slot);
  if (slides.length === 0) return null;
  return slides[getActiveSlideIndex(slot)] ?? slides[0];
}

/**
 * @param {Element} slot
 * @param {number} [forcedIndex]
 * @returns {boolean}
 */
function isFirstSlideActive(slot, forcedIndex) {
  const slides = getSlides(slot);
  if (slides.length === 0) return true;
  return getActiveSlideIndex(slot, forcedIndex) === 0;
}

/**
 * @param {Element} slot
 * @param {string | number} mediaId
 */
function selectSlideForMedia(slot, mediaId) {
  const slideshow = getSlideshow(slot);
  if (!slideshow || typeof slideshow.select !== 'function') return;

  const mediaEl = slot.querySelector(`[data-media-id="${mediaId}"]`);
  const slide = mediaEl?.closest('slideshow-slide');
  if (!slide) return;

  const slideId = slide.getAttribute('slide-id');
  if (slideId) {
    slideshow.select({ id: slideId }, undefined, { animate: true });
    return;
  }

  const slides = Array.from(slot.querySelectorAll('slideshow-slide'));
  const index = slides.indexOf(slide);
  if (index >= 0) {
    slideshow.select(index, undefined, { animate: true });
  }
}

/**
 * @param {Element} slot
 * @returns {Promise<void>}
 */
function waitForGalleryReady(slot) {
  return new Promise((resolve) => {
    const isReady = () => Boolean(getActiveSlide(slot)?.querySelector('.product-media--drywell-featured, .product-media'));

    if (isReady()) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      resolve();
    };

    const observer = new MutationObserver(() => {
      if (isReady()) finish();
    });

    observer.observe(slot, { childList: true, subtree: true });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (isReady()) {
          finish();
          return;
        }
        setTimeout(finish, 200);
      });
    });
  });
}

/**
 * @param {Element} media
 */
function activateDrywellFx(media) {
  media.classList.remove('drywell-active', 'drywell-enter');
  void media.offsetWidth;
  media.classList.add('drywell-active', 'drywell-enter');

  const onEnterEnd = (event) => {
    if (event.animationName !== 'drywellBurstOnce' && event.animationName !== 'drywellEnterGlow') return;
    media.classList.remove('drywell-enter');
    media.removeEventListener('animationend', onEnterEnd);
  };

  media.addEventListener('animationend', onEnterEnd);
  setTimeout(() => media.classList.remove('drywell-enter'), 1200);
}

/**
 * @param {Element} stage
 */
function pulseStage(stage) {
  if (!(stage instanceof HTMLElement)) return;
  stage.classList.remove('drywell-stage-pulse');
  void stage.offsetWidth;
  stage.classList.add('drywell-stage-pulse');
  setTimeout(() => stage.classList.remove('drywell-stage-pulse'), 800);
}

/**
 * @param {Element} slot
 * @param {{ burst?: boolean }} [options]
 */
function updateDrywellFeaturedFx(slot, options = {}) {
  const { burst = false } = options;
  const prevActive = slot.querySelector('.product-media--drywell-featured.drywell-active');

  slot.querySelectorAll('.product-media--drywell-featured').forEach((media) => {
    media.classList.remove('drywell-active', 'drywell-enter');
  });

  const activeSlide = getActiveSlide(slot);
  const activeMedia = activeSlide?.querySelector('.product-media--drywell-featured');

  if (!activeMedia) return;

  const shouldBurst = burst || activeMedia !== prevActive;
  if (shouldBurst) {
    activateDrywellFx(activeMedia);
    const stage = slot.closest('.sw-media-stage');
    if (burst) pulseStage(stage);
  } else {
    activeMedia.classList.add('drywell-active');
  }
}

/**
 * @param {Element} slot
 * @param {HTMLElement | null} banner
 * @param {{ burst?: boolean, index?: number }} [options]
 */
function updateMediaOverlays(slot, banner, options = {}) {
  const showOnFirstSlide = isFirstSlideActive(slot, options.index);
  const stage = slot.closest('.sw-media-stage');

  slot.classList.toggle('sw-media-gallery-slot--first-slide', showOnFirstSlide);
  stage?.classList.toggle('sw-media-stage--first-slide', showOnFirstSlide);

  if (banner) {
    banner.hidden = !showOnFirstSlide;
    banner.setAttribute('aria-hidden', showOnFirstSlide ? 'false' : 'true');
    banner.classList.toggle('sw-trust-banner--hidden', !showOnFirstSlide);
  }

  updateDrywellFeaturedFx(slot, options);
}

/**
 * @param {Element} scope
 * @param {Element} slot
 * @param {HTMLElement | null} banner
 * @param {CustomEvent} [event]
 */
async function handleVariantMediaChange(scope, slot, banner, event) {
  const token = ++variantFxToken;
  const featuredMediaId = event?.detail?.resource?.featured_media?.id;

  updateFxColor(scope);

  await waitForGalleryReady(slot);
  if (token !== variantFxToken) return;

  if (featuredMediaId) {
    suppressSlideBurst = true;
    selectSlideForMedia(slot, featuredMediaId);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    suppressSlideBurst = false;
    if (token !== variantFxToken) return;
  }

  updateMediaOverlays(slot, banner, { burst: true });
}

/**
 * @param {ParentNode} [root]
 */
function initTrustBanner(root = document) {
  const scope = root.querySelector?.('.sleepwhisper-product') || (root.classList?.contains('sleepwhisper-product') ? root : null);

  if (!scope) return;

  const slot = scope.querySelector('.sw-media-gallery-slot');
  const banner = scope.querySelector('[data-sw-trust-banner]');

  if (!slot) return;

  if (slot.dataset.swMediaInit === 'true') return;
  slot.dataset.swMediaInit = 'true';

  const bannerEl = banner instanceof HTMLElement ? banner : null;

  const update = (options = {}) => {
    updateFxColor(scope);
    updateMediaOverlays(slot, bannerEl, options);
  };

  update({ burst: true });

  const onSlideshowSelect = (event) => {
    if (!(event.target instanceof Element) || !slot.contains(event.target)) return;
    if (suppressSlideBurst) {
      update({ index: event.detail?.index });
      return;
    }
    update({ burst: true, index: event.detail?.index });
  };

  slot.addEventListener(SlideshowSelectEvent.eventName, onSlideshowSelect);
  scope.addEventListener(SlideshowSelectEvent.eventName, onSlideshowSelect);

  // Thumbnail / control clicks can settle after select; re-sync from slideshow.current.
  slot.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('slideshow-controls, .slideshow-controls__thumbnail, button[name="next"], button[name="previous"]')) {
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const slideshow = getSlideshow(slot);
        update({ index: typeof slideshow?.current === 'number' ? slideshow.current : undefined });
      });
    });
  });

  scope.addEventListener(ThemeEvents.variantSelected, () => {
    updateFxColor(scope);
  });

  scope.addEventListener(ThemeEvents.variantUpdate, (event) => {
    handleVariantMediaChange(scope, slot, bannerEl, event);
  });
}

function initRatingScroll(root = document) {
  const scope = root.querySelector?.('.sleepwhisper-product') || (root.classList?.contains('sleepwhisper-product') ? root : root);

  scope.querySelectorAll('[data-sw-rating-link]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;

    link.addEventListener('click', (event) => {
      const reviews = document.getElementById('customer-reviews');
      if (!reviews) return;

      event.preventDefault();
      reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
      reviews.focus({ preventScroll: true });
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initTrustBanner();
  initRatingScroll();
});
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initTrustBanner(event.target);
    initRatingScroll(event.target);
  }
});
